import { TFunction } from 'i18next';
import { InstalledBaseNodeType, InstalledBaseNodeTypeKey } from '../codegen/graphql';
import { getInstalledBaseNodeTypeName } from './getInstalledBaseNodeTypeName';

/**
 * Decides what an *empty* installed base column offers the user.
 *
 * Pure on purpose: the branch matrix (0/1/many recommendations × asset allowed or not ×
 * other types or not) is the whole feature, so it is unit-tested instead of click-tested.
 * Input is the already-permission- and rule-filtered set of creatable child types (see
 * `useAllowedChildNodeTypes`), so this function knows nothing about queries or rules.
 *
 * ASSET is deliberately kept out of the type count: it always gets its own dedicated
 * "Add Asset" action, so a parent recommending SYSTEM *and* ASSET still counts as a
 * single recommendation and gets the type-specific primary button.
 */

/** How many recommended types are listed as chips before collapsing into "+N more". */
export const MAX_BADGES = 4;

export type GuidanceAction =
    /** Open the create form for this specific type, skipping the type selection. */
    | { kind: 'type'; type: InstalledBaseNodeType }
    /** Open the node type selection screen (never auto-skipped). */
    | { kind: 'select' }
    /** Open the asset registration flow, skipping the type selection. */
    | { kind: 'asset'; type: InstalledBaseNodeType };

export type EmptyColumnGuidance =
    /** Nothing can be created here (leaf node, or every allowed type has max: 0). */
    | { variant: 'none' }
    /**
     * One obvious type to add. `headlineType` names it; `onlyOption` is true when it is
     * the *only* creatable node type, which switches the body copy from "usually
     * contains" to "can only contain".
     */
    | {
          variant: 'single';
          headlineType: InstalledBaseNodeType;
          onlyOption: boolean;
          primary: GuidanceAction;
          secondaries: GuidanceAction[];
      }
    /**
     * Several types are possible. `badgeTypes` are the recommended ones, or — when
     * nothing is recommended — all creatable ones; `hasRecommendations` distinguishes
     * the two for the eyebrow copy.
     */
    | {
          variant: 'generic';
          hasRecommendations: boolean;
          badgeTypes: InstalledBaseNodeType[];
          hiddenBadgeCount: number;
          primary: GuidanceAction;
          secondaries: GuidanceAction[];
      };

/**
 * @param allowedTypes node types that may actually be created under the parent —
 *        whitelisted by `nodeRules.childRules` and not at their count limit.
 */
export const resolveEmptyColumnGuidance = (
    allowedTypes: InstalledBaseNodeType[]
): EmptyColumnGuidance => {
    const assetType = allowedTypes.find((nt) => nt.key === InstalledBaseNodeTypeKey.ASSET);
    const nodes = allowedTypes.filter((nt) => nt.key !== InstalledBaseNodeTypeKey.ASSET);
    const recommendedNodes = nodes.filter((nt) => nt.recommended);

    const assetAction: GuidanceAction | undefined = assetType
        ? { kind: 'asset', type: assetType }
        : undefined;

    if (!nodes.length && !assetType) {
        return { variant: 'none' };
    }

    // Assets are the only thing that fits here — the asset flow becomes the primary.
    if (!nodes.length && assetType) {
        return {
            variant: 'single',
            headlineType: assetType,
            onlyOption: true,
            primary: assetAction!,
            secondaries: [],
        };
    }

    // A single obvious choice: either exactly one recommendation, or no recommendation
    // at all but only one type to pick from.
    const singleType =
        recommendedNodes.length === 1
            ? recommendedNodes[0]
            : recommendedNodes.length === 0 && nodes.length === 1
                ? nodes[0]
                : undefined;

    if (singleType) {
        const secondaries: GuidanceAction[] = [];
        // Only worth offering the selection screen when it holds more than this one type.
        if (nodes.length > 1) secondaries.push({ kind: 'select' });
        if (assetAction) secondaries.push(assetAction);
        return {
            variant: 'single',
            headlineType: singleType,
            onlyOption: nodes.length === 1 && !assetType,
            primary: { kind: 'type', type: singleType },
            secondaries,
        };
    }

    const hasRecommendations = recommendedNodes.length > 0;
    const badgeCandidates = hasRecommendations ? recommendedNodes : nodes;
    return {
        variant: 'generic',
        hasRecommendations,
        badgeTypes: badgeCandidates.slice(0, MAX_BADGES),
        hiddenBadgeCount: Math.max(0, badgeCandidates.length - MAX_BADGES),
        primary: { kind: 'select' },
        secondaries: assetAction ? [assetAction] : [],
    };
};

/**
 * Button label for an action. Shared so the empty state and the nameplate create button
 * can't drift apart; `t` is passed in rather than hooked so this file stays React-free
 * (same pattern as {@link getInstalledBaseNodeTypeName}).
 */
export const getGuidanceActionLabel = (
    action: GuidanceAction,
    t: TFunction,
    parentTypeKey?: InstalledBaseNodeTypeKey
): string => {
    switch (action.kind) {
        case 'asset':
            // Below an asset, the added asset is a sub-asset — name it as such.
            return parentTypeKey === InstalledBaseNodeTypeKey.ASSET
                ? t('Create Sub-Asset')
                : t('Add Asset');
        case 'select':
            return t('Create Node');
        case 'type':
            return t('Create {{typeName}}', {
                typeName: getInstalledBaseNodeTypeName(action.type, t),
            });
    }
};
